import ExceptionEntity from "../../../../../domain/entities/ExceptionEntity";
import { Either, left, right } from "fp-ts/lib/Either";
import { Auth } from "aws-amplify";
import Constants, { ConstantsName } from "../../../../../constants/Constants";
import di from "../../../../../di/DependencyInjection";
import { ProvisionUserPayload, ProvisionUserResponse } from "../../../../../domain/entities/ProvisionUserEntity";

const extractEmailsAndMessages = (data: any) => {
    const result: any = [];
    data.details.forEach((detail: any) => {
        const userInfo = detail[0]; // Accessing the user information object
        if (userInfo && userInfo.data && userInfo.data.email && userInfo.message && detail[1] !== 200) {
            result.push({
                email: userInfo.data.email,
                message: userInfo.message
            });
        }
    });
    return result;
};

const getAuthHeaders = async (): Promise<Record<string, string>> => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'Content-Type': 'application/json',
        'Authorization': token,
    };
};

const CreateUsersApiImpl = async (data: any[]): Promise<Either<ExceptionEntity, string>> => {
    try {
        const baseUrl = di.get<Constants>(ConstantsName).HOST_API_USERS_URL;
        const headers = await getAuthHeaders();
        
        const response = await fetch(`${baseUrl}/usersetup`, {
            method: 'POST',
            headers,
            body: JSON.stringify(data),
        });

        const datatest = await response.json();

        if (!response.ok) {
            throw new Error(datatest.message);
        }

        if (response.status === 207) {
            const emailsAndMessages = extractEmailsAndMessages(datatest);
            const usersFilterd = emailsAndMessages.map((user: any) => user.email).join(",");
            throw new Error(`Error creating some users: ${usersFilterd}`);
        }

        return right(datatest);
        
    } catch (error: any) {
        const exception: ExceptionEntity = {message: error.message};
        
        return left(exception);
    }
}

export const ProvisionUserApiImpl = async (data: ProvisionUserPayload): Promise<Either<ExceptionEntity, ProvisionUserResponse>> => {
    const TIMEOUT_MS = 30000;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

    try {
        const baseUrl = di.get<Constants>(ConstantsName).HOST_API_USERS_URL;
        const headers = await getAuthHeaders();

        const response = await fetch(`${baseUrl}/provision-user`, {
            method: 'POST',
            headers,
            body: JSON.stringify(data),
            signal: controller.signal,
        });

        const responseData = await response.json();

        if (response.status === 201) {
            return right(responseData as ProvisionUserResponse);
        }

        // Map 400/403/409/500 to Left<ExceptionEntity>
        let message = responseData.message || 'An unexpected error occurred';

        if (response.status === 400 && responseData.errors) {
            message = responseData.errors
                .map((err: { field: string; message: string }) => `${err.field}: ${err.message}`)
                .join('; ');
        }

        if (response.status === 409) {
            const existingIds = [
                responseData.user_id ? `user_id: ${responseData.user_id}` : '',
                responseData.systemid ? `systemid: ${responseData.systemid}` : '',
            ].filter(Boolean).join(', ');
            message = `${message}${existingIds ? ` (${existingIds})` : ''}`;
        }

        const exception: ExceptionEntity = {
            code: String(response.status),
            message,
        };

        return left(exception);

    } catch (error: any) {
        const isTimeout = error.name === 'AbortError';
        const exception: ExceptionEntity = {
            code: isTimeout ? 'TIMEOUT' : 'NETWORK_ERROR',
            message: isTimeout
                ? 'Request timed out after 30 seconds'
                : error.message || 'Network error occurred',
        };

        return left(exception);
    } finally {
        clearTimeout(timeoutId);
    }
};

export default CreateUsersApiImpl;
